import { useEffect, useMemo, useState } from "react"
import { ArrowLeft, Check, LoaderCircle, Search, Send } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { loadInvitationOptions, sendFreelancerInvitation } from "@/lib/freelancerInvitations"

import OutOfScopeRequestDialog from "./OutOfScopeRequestDialog"
import SampleRequestDialog from "./SampleRequestDialog"
import { cn } from "@/lib/utils"

function ProjectInvite({ open, freelancer, preselectOrderId, onOpenChange }) {
  const name = freelancer?.penName ?? "this freelancer"
  const [projects, setProjects] = useState([])
  const [projectQuery, setProjectQuery] = useState("")
  const [selectedOrderId, setSelectedOrderId] = useState(null)
  const [inviteType, setInviteType] = useState("")
  const [message, setMessage] = useState("")
  const [step, setStep] = useState("compose")
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  useEffect(() => {
    if (!open || !freelancer?.id) return undefined

    const controller = new AbortController()
    setProjects([])
    setProjectQuery("")
    setSelectedOrderId(null)
    setInviteType("")
    setMessage("")
    setStep("compose")
    setError("")
    setSuccess("")
    setLoading(true)

    loadInvitationOptions(freelancer.id, { signal: controller.signal })
      .then((data) => {
        const loaded = data.projects ?? []
        setProjects(loaded)

        // The order the customer launched from (Find Freelancers' `?order=`
        // hint). Preselection is a match against the list just loaded for THIS
        // freelancer, applied through the same seam a click uses — an id that
        // isn't in the list, is stale, or has no eligible invitation type
        // selects nothing, because selectProject already refuses those.
        const hinted = loaded.find((project) => project.id === preselectOrderId)
        if (hinted) selectProject(hinted)
      })
      .catch((loadError) => {
        if (loadError.name !== "AbortError") setError(loadError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })

    return () => controller.abort()
  }, [open, freelancer?.id, preselectOrderId])

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedOrderId),
    [projects, selectedOrderId]
  )
  const visibleProjects = useMemo(() => {
    const query = projectQuery.trim().toLowerCase()
    if (!query) return projects

    return projects.filter((project) =>
      [project.badge, project.title, project.service]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    )
  }, [projectQuery, projects])
  const selectedType = selectedProject?.types.find((type) => type.key === inviteType)

  function selectProject(project) {
    const firstEligibleType = project.types.find((type) => type.eligible)
    if (!firstEligibleType) return
    setSelectedOrderId(project.id)
    setInviteType(firstEligibleType.key)
    setError("")
  }

  async function submit() {
    if (!selectedProject || !selectedType?.eligible || !message.trim() || submitting) return
    setSubmitting(true)
    setError("")
    try {
      const data = await sendFreelancerInvitation(freelancer.id, {
        orderId: selectedProject.id,
        type: selectedType.key,
        message: message.trim(),
      })
      setSuccess(data.message)
      setStep("sent")
    } catch (submitError) {
      setError(submitError.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (step === "sent") {
    return (
      <>
        <DialogHeader>
          <div className="mb-2 flex size-11 items-center justify-center rounded-full bg-done/10 text-done">
            <Check className="size-5" strokeWidth={2.5} />
          </div>
          <DialogTitle>Invitation sent</DialogTitle>
          <DialogDescription>{success}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </>
    )
  }

  if (step === "review") {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Review your invitation</DialogTitle>
          <DialogDescription>Confirm the project, invitation type and note before sending.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 rounded-[12px] border border-border bg-secondary/45 p-4 text-[13px]">
          <div><span className="text-ink-3">Freelancer</span><div className="font-semibold">{name}</div></div>
          <div><span className="text-ink-3">Project</span><div className="font-semibold">{selectedProject?.badge} · {selectedProject?.title}</div></div>
          <div><span className="text-ink-3">Invitation</span><div className="font-semibold">{selectedType?.label}</div></div>
          <div><span className="text-ink-3">Message</span><div className="mt-1 whitespace-pre-wrap">{message}</div></div>
        </div>
        {error && <p role="alert" className="text-[12.5px] text-destructive">{error}</p>}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => setStep("compose")} disabled={submitting}>
            <ArrowLeft className="size-4" />
            Back
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? <LoaderCircle className="size-4 animate-spin" /> : <Send className="size-4" />}
            {submitting ? "Sending…" : "Send invitation"}
          </Button>
        </DialogFooter>
      </>
    )
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Invite {name} to a project</DialogTitle>
        <DialogDescription>
          Choose one of your open projects. Eligibility is checked again when you send.
        </DialogDescription>
      </DialogHeader>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-[13px] text-ink-3">
          <LoaderCircle className="size-4 animate-spin" />
          Checking your projects…
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {projects.length > 0 && (
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-3" />
              <Input
                aria-label="Search projects"
                placeholder="Search by project, number or service"
                value={projectQuery}
                onChange={(event) => setProjectQuery(event.target.value)}
                className="pl-9"
              />
            </div>
          )}
          <div className="flex max-h-[300px] flex-col gap-2 overflow-y-auto pr-1">
            {projects.length === 0 && !error && (
              <div className="rounded-[12px] border border-border bg-secondary/40 p-5 text-center">
                <div className="text-[13.5px] font-semibold">No open projects to invite to</div>
                <p className="mt-1 text-[12.5px] text-ink-3">Start a project first, then return to invite {name}.</p>
                <Button asChild variant="outline" className="mt-4"><a href="/post">Start a project</a></Button>
              </div>
            )}
            {projects.length > 0 && visibleProjects.length === 0 && (
              <div className="rounded-[12px] border border-border bg-secondary/40 p-5 text-center text-[12.5px] text-ink-3">
                No open projects match that search.
              </div>
            )}
            {visibleProjects.map((project) => {
              const eligible = project.types.some((type) => type.eligible)
              const selected = selectedOrderId === project.id
              const reason = project.types.find((type) => type.reason)?.reason
              return (
                <button
                  key={project.id}
                  type="button"
                  disabled={!eligible}
                  onClick={() => selectProject(project)}
                  className={cn(
                    "rounded-[11px] border px-3.5 py-3 text-left transition-colors",
                    selected ? "border-primary bg-[var(--red-soft)]" : "border-border hover:bg-accent",
                    !eligible && "cursor-not-allowed opacity-55"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span className={cn("mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border", selected ? "border-primary bg-primary text-primary-foreground" : "border-line-2")}>
                      {selected && <Check className="size-2.5" strokeWidth={3} />}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-display text-[13.5px] font-semibold">{project.badge} · {project.title}</span>
                      <span className="mt-0.5 block text-[11.5px] text-ink-3">{eligible ? project.service : reason}</span>
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {selectedProject && (
        <div className="flex flex-col gap-2">
          <Label>Invitation type</Label>
          {selectedProject.types.map((type) => (
            <button
              key={type.key}
              type="button"
              disabled={!type.eligible}
              onClick={() => setInviteType(type.key)}
              className={cn(
                "rounded-[10px] border px-3.5 py-3 text-left",
                inviteType === type.key ? "border-primary bg-[var(--red-soft)]" : "border-border",
                !type.eligible && "cursor-not-allowed opacity-55"
              )}
            >
              <span className="block text-[13px] font-semibold">{type.label}</span>
              <span className="mt-0.5 block text-[11.5px] text-ink-3">{type.eligible ? type.description : type.reason}</span>
            </button>
          ))}
          <Label htmlFor="freelancer-invite-message" className="mt-2">Message</Label>
          <Textarea
            id="freelancer-invite-message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Share the project context, timing and why you'd like to work together."
            rows={4}
            maxLength={2_000}
            required
          />
        </div>
      )}

      {error && <p role="alert" className="text-[12.5px] text-destructive">{error}</p>}
      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button
          onClick={() => setStep("review")}
          disabled={!selectedProject || !selectedType?.eligible || !message.trim() || loading}
        >
          Review invitation
        </Button>
      </DialogFooter>
    </>
  )
}

// The forms a profile can open on one freelancer. Each takes the same three
// props; the sample form also takes a resumed notice and onSent;
// anything not named here falls through to the project invitation.
const FREELANCER_FORMS = {
  sample: SampleRequestDialog,
  outOfScope: OutOfScopeRequestDialog,
}

export default function FunnelDeferredDialog({
  open,
  onOpenChange,
  freelancer,
  kind = "invite",
  preselectOrderId = null,
  notice,
  onSent,
}) {
  const Form = FREELANCER_FORMS[kind]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        {Form ? (
          <Form open={open} freelancer={freelancer} onOpenChange={onOpenChange} notice={notice} onSent={onSent} />
        ) : (
          <ProjectInvite
            open={open}
            freelancer={freelancer}
            preselectOrderId={preselectOrderId}
            onOpenChange={onOpenChange}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
